import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { Button, Checkbox } from "common/components";
import { DataSchema } from "../../data/models";
import { snowflakeSchemas } from "../../data";
import { MainRoutes } from "common/config/route.config";
import "./AvailableDataTable.scss";

interface AvailableDataTableProps {
  connectionName?: string;
  onSelectData?: (schema: DataSchema, selectedColumns: string[]) => void;
  onClose?: () => void;
}

const AvailableDataTable: React.FC<AvailableDataTableProps> = ({
  connectionName: propConnectionName,
  onSelectData,
  onClose
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get connection name from props or URL params
  const connectionName =
    propConnectionName ||
    searchParams.get("connection") ||
    "Default Connection";

  const [selectedTable, setSelectedTable] = useState<DataSchema | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set()
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [previewData, setPreviewData] = useState(false);

  // Restore state from URL parameters when component mounts
  useEffect(() => {
    const tableParam = searchParams.get("table");
    const columnsParam = searchParams.get("columns");

    if (tableParam) {
      const foundSchema = snowflakeSchemas.find(
        (s) => s.tableName === tableParam
      );
      if (foundSchema) {
        setSelectedTable(foundSchema);

        if (columnsParam) {
          const columnsList = columnsParam
            .split(",")
            .filter((col) => col.trim());
          setSelectedColumns(new Set(columnsList));
        }
      }
    }
  }, [searchParams]);

  const handleTableSelect = (schema: DataSchema) => {
    setSelectedTable(schema);
    setSelectedColumns(new Set());
  };

  const handleColumnToggle = (columnName: string) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(columnName)) {
      newSelected.delete(columnName);
    } else {
      newSelected.add(columnName);
    }
    setSelectedColumns(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTable) {
      if (selectedColumns.size === selectedTable.columns.length) {
        setSelectedColumns(new Set());
      } else {
        setSelectedColumns(new Set(selectedTable.columns.map((c) => c.name)));
      }
    }
  };

  const handleContinue = () => {
    if (selectedTable && selectedColumns.size > 0) {
      if (onSelectData) {
        onSelectData(selectedTable, Array.from(selectedColumns));
      } else {
        // Default behavior for route usage - navigate to filter builder
        const columnsParam = Array.from(selectedColumns).join(",");
        navigate(
          `${MainRoutes.DATA_FILTER_BUILDER}?table=${selectedTable.tableName}&columns=${columnsParam}`
        );
      }
    }
  };

  //   const handleClose = () => {
  //     if (onClose) {
  //       onClose();
  //     } else {
  //       // Default behavior for route usage - navigate back
  //       navigate(-1);
  //     }
  //   };

  const filteredSchemas = snowflakeSchemas.filter((schema) =>
    schema.tableName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <HeaderNew showSearch={false} />

      <div className="available-data-table-hub">
        <div className="hub-content">
          <HubHeader
            title="Data Integration Hub"
            description="Centralized platform for seamless data integration and workflow management."
            showBackButton={true}
            backRoute="/data-integration-hub"
          />

          <div className="section-header">
            <div className="section-info">
              <h2>Explore Data Sources</h2>
              <p>
                Connection:{" "}
                <span className="font-medium">{connectionName}</span>
              </p>
            </div>
          </div>

          <div className="table-explorer-content">
            <div className="explorer-layout">
              {/* Left Panel - Tables */}
              <div className="tables-panel">
                <div className="panel-header">
                  <h2>Available Tables</h2>
                  <div className="search-container">
                    <SearchIcon className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search tables..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="tables-list">
                  {filteredSchemas.map((schema) => (
                    <div
                      key={schema.tableName}
                      className={`table-item ${
                        selectedTable?.tableName === schema.tableName
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleTableSelect(schema)}
                    >
                      <div className="table-item-content">
                        <StorageIcon className="table-icon" />
                        <div className="table-info">
                          <div className="table-name">{schema.tableName}</div>
                          <div className="table-meta">
                            {schema.columns.length} columns ·{" "}
                            {schema.recordCount.toLocaleString()} rows
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Panel - Column Selection */}
              <div className="columns-panel">
                {selectedTable ? (
                  <>
                    <div className="column-header">
                      <div className="table-title">
                        <h2>{selectedTable.tableName}</h2>
                        <p className="selection-info">
                          {selectedColumns.size} of{" "}
                          {selectedTable.columns.length} columns selected
                        </p>
                      </div>
                      <div className="header-actions">
                        <button
                          onClick={handleSelectAll}
                          className="action-button"
                        >
                          {selectedColumns.size === selectedTable.columns.length
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                        <button
                          onClick={() => setPreviewData(!previewData)}
                          className="action-button preview-button"
                        >
                          <VisibilityIcon /> Preview
                        </button>
                      </div>
                    </div>

                    <div className="columns-table-wrapper">
                      <table className="columns-table">
                        <thead>
                          <tr>
                            <th className="checkbox-column"></th>
                            <th>Column Name</th>
                            <th>Data Type</th>
                            <th>Description</th>
                            {previewData && <th>Sample</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTable.columns.map((column) => (
                            <tr key={column.name}>
                              <td>
                                <Checkbox
                                  checked={selectedColumns.has(column.name)}
                                  onChange={() =>
                                    handleColumnToggle(column.name)
                                  }
                                />
                              </td>
                              <td className="column-name">
                                <span className="column-name-text">
                                  {column.name}
                                </span>
                              </td>
                              <td>
                                <span className="data-type-badge">
                                  {column.type}
                                </span>
                              </td>
                              <td className="column-description">
                                {column.description}
                              </td>
                              {previewData && (
                                <td>
                                  <code className="sample-data">
                                    {column.sample}
                                  </code>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="empty-content">
                    <StorageIcon className="empty-icon" />
                    <h3>Select a Table</h3>
                    <p>
                      Choose a table from the left panel to view and select
                      columns
                    </p>
                  </div>
                )}
              </div>
            </div>

            {selectedTable && (
              <div className="action-buttons">
                <button
                  onClick={handleContinue}
                  disabled={selectedColumns.size === 0}
                  className="continue-button"
                >
                  Continue to Filtering <ArrowForwardIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AvailableDataTable;
